import './coapplicantReviewPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import CoapplicantEmploymentSection from './CoapplicantEmploymentSection';
import CoapplicantIncomeSection from './CoapplicantIncomeSection';
import CoapplicantPersonalSection from './CoapplicantPersonalSection';
import CoapplicantResidenceSection from './CoapplicantResidenceSection';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();
  const { handleSubmit } = useForm();

  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const previousStep = (): void => {
    navigate(previous());
  };

  const formSubmit = (): void => {
    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-review-page" sx={theme.pageLayout}>
        <header data-testid={locator.pages.coapplicantReview.header.root}>
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantReview.header.previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={locator.pages.coapplicantReview.header.drawerButton}
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.coapplicantReview.progress}
            value={progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantReview.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantReview.form.h1}>
                {t('CoapplicantReviewPage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantReview.form.h2}>
                {t('CoapplicantReviewPage.subtitle')}
              </h2>

              <div className="content" style={{ marginBottom: '3rem' }}>
                <CoapplicantPersonalSection />
                <CoapplicantResidenceSection />
                <CoapplicantEmploymentSection />
                <CoapplicantIncomeSection />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantReview.form.submitButton
                  }
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantReviewPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer data-testid={locator.pages.coapplicantReview.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantReviewPage';
